import type { WebKilnApiClient } from './api-client';
import type { CloudEditorSync } from './cloud-editor-sync';
import { bindDialog } from './dialog';

export function renderPublishDialog(
  client: WebKilnApiClient,
  siteId: string,
  sync: CloudEditorSync,
  onPublished: () => void,
): void {
  if (document.querySelector('[data-publish-dialog]')) return;
  const backdrop = document.createElement('div');
  backdrop.className = 'modal-backdrop';
  backdrop.dataset.publishDialog = 'true';
  backdrop.innerHTML = `<section class="setup-modal publish-modal" role="dialog" aria-modal="true" aria-labelledby="publishTitle"><div class="modal-head"><div><p class="eyebrow">Publish website</p><h2 id="publishTitle">Ready to go live?</h2></div><button type="button" class="mini-btn" data-close-publish aria-label="Close publish dialog">×</button></div><form data-publish-form><p class="modal-copy">Choose who can view your website. Your latest saved version will be published.</p><label class="field">Visibility<select name="visibility" data-publish-visibility><option value="public">Public</option><option value="private">Private · password protected</option></select></label><p class="field-help" data-access-help>Anyone with the link can view your website.</p><label class="field" data-password-field hidden>Site password<input name="password" data-publish-password type="password" autocomplete="new-password" placeholder="Enter a site password" /></label><p class="cloud-error" data-publish-error role="alert" hidden></p><div class="modal-actions"><button type="button" class="ghost-btn" data-close-publish>Cancel</button><button type="submit" class="primary-btn" data-confirm-publish>Publish website</button></div></form><div class="publish-progress" data-publish-progress role="status" aria-live="polite" hidden><span class="publish-spinner" aria-hidden="true"></span><p>Saving your changes and publishing your website…</p></div><div class="publish-success" data-publish-success hidden><span class="publish-check" aria-hidden="true">✓</span><p class="modal-copy" data-success-copy>Your website is live and ready to share.</p><div class="modal-actions"><a class="primary-btn" data-view-live target="_blank" rel="noopener noreferrer">View live site</a><button type="button" class="ghost-btn" data-continue-editing>Continue editing</button></div></div></section>`;
  document.body.append(backdrop);
  let busy = false;
  let touched = false;
  const close = () => {
    if (busy) return;
    releaseFocus();
    backdrop.remove();
  };
  const releaseFocus = bindDialog(backdrop, close);
  backdrop
    .querySelectorAll('[data-close-publish], [data-continue-editing]')
    .forEach((button) => button.addEventListener('click', close));
  const form = backdrop.querySelector<HTMLFormElement>('form')!;
  const selector = backdrop.querySelector<HTMLSelectElement>('[data-publish-visibility]')!;
  const confirm = backdrop.querySelector<HTMLButtonElement>('[data-confirm-publish]')!;
  confirm.disabled = true;
  const password = backdrop.querySelector<HTMLInputElement>('[data-publish-password]')!;
  const error = backdrop.querySelector<HTMLElement>('[data-publish-error]')!;
  const progress = backdrop.querySelector<HTMLElement>('[data-publish-progress]')!;
  const title = backdrop.querySelector<HTMLElement>('#publishTitle')!;
  const updateVisibility = () => {
    const privateSite = selector.value === 'private';
    backdrop.querySelector<HTMLElement>('[data-password-field]')!.hidden = !privateSite;
    password.required = privateSite;
    password.disabled = !privateSite;
    backdrop.querySelector('[data-access-help]')!.textContent = privateSite
      ? 'Visitors will need this password to view your website.'
      : 'Anyone with the link can view your website.';
  };
  selector.addEventListener('change', () => {
    touched = true;
    confirm.disabled = false;
    error.hidden = true;
    updateVisibility();
    if (selector.value === 'private') password.focus();
  });
  void client
    .getPublishStatus(siteId)
    .then((status) => {
      if (!touched && !busy && backdrop.isConnected) {
        selector.value = status.visibility ?? 'public';
        updateVisibility();
      }
      if (backdrop.isConnected && !busy) confirm.disabled = false;
    })
    .catch(() => {
      if (!touched && backdrop.isConnected && !busy) {
        const placeholder = new Option('Choose visibility', '');
        placeholder.disabled = true;
        selector.prepend(placeholder);
        selector.value = '';
        error.textContent =
          'Could not check current visibility. Choose Public or Private to continue.';
        error.hidden = false;
      }
    });
  updateVisibility();
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (busy) return;
    const visibility = selector.value as 'public' | 'private';
    password.setCustomValidity(
      visibility === 'private' && !password.value.trim() ? 'Enter a site password.' : '',
    );
    if (!form.reportValidity()) return;
    error.hidden = true;
    busy = true;
    form.hidden = true;
    progress.hidden = false;
    progress.tabIndex = -1;
    progress.focus();
    title.textContent = 'Publishing your website…';
    backdrop.querySelector<HTMLButtonElement>('.modal-head [data-close-publish]')!.disabled = true;
    try {
      await sync.saveBeforePublish();
      const result = await client.publishSite(
        siteId,
        sync.currentRevision,
        visibility === 'private' ? password.value : undefined,
        visibility,
      );
      busy = false;
      progress.hidden = true;
      title.textContent = 'Website published';
      backdrop.querySelector<HTMLElement>('[data-publish-success]')!.hidden = false;
      backdrop.querySelector('[data-success-copy]')!.textContent =
        visibility === 'private'
          ? 'Your private website is live. Visitors will need the password to enter.'
          : 'Your public website is live and ready to share.';
      const link = backdrop.querySelector<HTMLAnchorElement>('[data-view-live]')!;
      if (result.publicUrl) link.href = result.publicUrl;
      else link.hidden = true;
      backdrop.querySelector<HTMLButtonElement>('.modal-head [data-close-publish]')!.disabled =
        false;
      backdrop.querySelector<HTMLButtonElement>('[data-continue-editing]')!.focus();
      onPublished();
    } catch (publishError) {
      busy = false;
      progress.hidden = true;
      form.hidden = false;
      title.textContent = 'Ready to go live?';
      error.textContent =
        publishError instanceof Error
          ? publishError.message
          : 'Publishing failed. Please try again.';
      error.hidden = false;
      backdrop.querySelector<HTMLButtonElement>('.modal-head [data-close-publish]')!.disabled =
        false;
      backdrop.querySelector<HTMLButtonElement>('[data-confirm-publish]')!.focus();
    }
  });
  password.addEventListener('input', () => password.setCustomValidity(''));
}
